/**
 * Live "agent is running" banner shown when the user opens a session whose
 * background run is still streaming. The chat itself is empty until the
 * run terminates and `save_message` writes the assistant response to
 * Cosmos — without this banner the user sees a blank page and assumes
 * the agent died.
 *
 * Source-of-truth: the `agent_run` registry (started_at, status) plus the
 * LangGraph checkpoint hydrated into `useAIExplorerStore` (todos).
 */
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AgentRunModel } from "@/api/api"

import { Loader2 } from "@/components/icons"
import {
  formatAgentRunDateTime,
  formatAgentRunElapsed,
} from "@/features/ai-explorer/utils/agent-run-time"
import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"

interface AgentRunningBannerProps {
  agentRun: AgentRunModel
  sessionType: string
}

export function AgentRunningBanner({ agentRun, sessionType }: AgentRunningBannerProps) {
  const { i18n, t } = useTranslation()
  const explorerStore = useAIExplorerStore(sessionType)
  // Read directly from the run's session — the banner is mounted when the
  // user is on this session, so its slot is the source of truth.
  const todos = explorerStore((state) => getDeepStateForSession(state, agentRun.session_id).todos)

  // 1Hz tick so the elapsed counter updates while the user watches —
  // no other state changes that often, so the banner re-renders alone.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  const total = todos.length
  const done = todos.filter((todo) => todo.status === "completed").length
  const inProgress = todos.find((todo) => todo.status === "in_progress")
  const inProgressLabel = inProgress?.content ?? inProgress?.text ?? null
  const startedLabel = formatAgentRunDateTime(agentRun.started_at, i18n.language)
  const elapsedLabel = formatAgentRunElapsed(agentRun.started_at, null)

  return (
    <section
      aria-live="polite"
      className="border-border bg-card mx-auto my-8 flex w-full max-w-xl flex-col items-center gap-3 rounded-lg border px-6 py-8 text-center"
    >
      <Loader2 className="text-primary size-6 animate-spin motion-reduce:animate-none" />
      <h2 className="text-foreground text-base font-semibold">
        {t("deepExplorer.banner_running_title", {
          defaultValue: "Agent is running",
        })}
      </h2>
      <p className="text-muted-foreground text-sm">
        {t("deepExplorer.banner_running_subtitle", {
          defaultValue:
            "Reopen this chat after it finishes to read the full answer. Progress updates live in the side panel.",
        })}
      </p>
      <dl className="text-muted-foreground mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs tabular-nums">
        {startedLabel && agentRun.started_at ? (
          <div>
            <dt className="sr-only">
              {t("deepExplorer.banner_started_label", { defaultValue: "Started" })}
            </dt>
            <dd>
              {t("deepExplorer.banner_started_prefix", {
                defaultValue: "Started",
              })}{" "}
              <time dateTime={agentRun.started_at}>{startedLabel}</time>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="sr-only">
            {t("deepExplorer.banner_elapsed_label", { defaultValue: "Elapsed" })}
          </dt>
          <dd>
            {t("deepExplorer.banner_elapsed_value", {
              defaultValue: "{{elapsed}} elapsed",
              elapsed: elapsedLabel,
            })}
          </dd>
        </div>
        {total > 0 && (
          <div>
            <dt className="sr-only">
              {t("deepExplorer.banner_progress_label", { defaultValue: "Progress" })}
            </dt>
            <dd>
              {t("deepExplorer.banner_progress_value", {
                defaultValue: "{{done}}/{{total}} steps",
                done,
                total,
              })}
            </dd>
          </div>
        )}
      </dl>
      {inProgressLabel && (
        <p className="text-foreground mt-1 line-clamp-2 w-full max-w-md text-xs break-words">
          {t("deepExplorer.banner_current_step", {
            defaultValue: "Now: {{step}}",
            step: inProgressLabel,
          })}
        </p>
      )}
    </section>
  )
}
